/**
 * @fileoverview Saved recordings: the one list of takes waiting in this
 * browser (iOS review B6).
 *
 * A recording is written to the upload queue before its upload starts, so a
 * save that cannot finish is kept here rather than lost. Each row says what
 * it is waiting for and offers the fix that applies: Try again, Request
 * consent, Plan & usage, Review notice. Send all at the top sends every take
 * that can go. Hans deletes nothing here; Discard is the instructor's.
 *
 * Reached from Home's one-line card, the top of the Lessons page and the
 * recorder's saved stage. A browser can be shared, so only the signed-in
 * instructor's own recordings are listed, sent or discarded.
 */
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CloudOff, RefreshCw, FileText, Lock, Clock, MinusCircle, AlertCircle, CloudUpload, CheckCircle2 } from "lucide-react";
import { AuthContext } from "@/App";
import api, { getErrorMessage } from "@/lib/api";
import { uploadQueue } from "@/lib/uploadQueue";
import { performUpload } from "@/lib/recordingUpload";
import { blockedReason, savedFix, clipLength, savedWhen } from "@/lib/savedRecordings";
import { useAiDisclosureGate } from "@/components/AiDisclosureDialog";
import { Button, Card, Eyebrow } from "@/components/studio/primitives";

/** The icon for what a row waits on. */
const REASON_ICON = {
  network: CloudOff,
  processing: RefreshCw,
  ai_disclosure_required: FileText,
  consent_required: Lock,
  minutes_exhausted: Clock,
  ai_not_included: Clock,
  pro_required: Clock,
  feature_disabled: MinusCircle,
  rejected: AlertCircle,
};

const studentName = (s) => `${s?.first_name || ""} ${s?.last_name || ""}`.trim();

/**
 * @returns {React.ReactElement}
 */
export default function SavedRecordingsPage() {
  const { user, setUser, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const accountId = user?.user_id || null;
  const [items, setItems] = useState(null);
  const [students, setStudents] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  // A send is a send to the AI processors, like a new recording.
  const { ensureAiDisclosure, aiDisclosureDialog } = useAiDisclosureGate({ user, setUser, refreshUser });

  const load = useCallback(async () => {
    try {
      setItems(await uploadQueue.listForAccount(accountId));
    } catch (_e) {
      setItems([]);
    }
  }, [accountId]);

  useEffect(() => {
    let on = true;
    uploadQueue.listForAccount(accountId)
      .then((rows) => { if (on) setItems(rows); })
      .catch(() => { if (on) setItems([]); });
    return () => { on = false; };
  }, [accountId]);

  const fetchStudents = useCallback(async (signal) => {
    try {
      const { data } = await api.get("/students", signal ? { signal } : {});
      setStudents(Array.isArray(data) ? data : []);
    } catch (_e) {
      // The rows still list; consent fixes simply read as Try again.
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchStudents(controller.signal);
    return () => controller.abort();
  }, [fetchStudents]);

  const studentFor = (entry) => (entry.kind === "class" ? null : students.find((s) => s.student_id === entry.studentId) || null);

  /** Open the lesson a sent take became. */
  const openLesson = (entry, lessonId) => {
    navigate(entry.kind === "class" ? `/lesson/${lessonId}?kind=class` : `/lesson/${lessonId}`);
  };

  /**
   * Send one take. A success removes it and opens its lesson; a failure
   * writes the new reason on the row. Resolves to the blocked reason, or
   * null on success, so Send all can tell what still needs the AI notice.
   */
  const send = async (entry) => {
    try {
      const lessonId = await performUpload(entry);
      await uploadQueue.remove(entry.id).catch(() => {});
      setItems((rows) => (rows || []).filter((row) => row.id !== entry.id));
      openLesson(entry, lessonId);
      return null;
    } catch (error) {
      const blocked = blockedReason(error);
      await uploadQueue.setBlocked(entry.id, blocked).catch(() => {});
      setItems((rows) => (rows || []).map((row) => (row.id === entry.id ? { ...row, blocked } : row)));
      if (blocked.code === "consent_required") fetchStudents();
      return blocked;
    }
  };

  /** Send one take from its row; the AI notice is asked first, and again on a refusal. */
  const sendOne = async (entry, { force = false } = {}) => {
    if (busyId || sendingAll) return;
    setBusyId(entry.id);
    try {
      if (!(await ensureAiDisclosure(force ? { force: true } : undefined))) return;
      let blocked = await send(entry);
      if (blocked?.code === "ai_disclosure_required" && (await ensureAiDisclosure({ force: true }))) {
        blocked = await send(entry);
      }
    } finally {
      setBusyId(null);
    }
  };

  /** Send every take that can go; the rows that cannot keep their reasons. */
  const sendAll = async () => {
    if (sendingAll || busyId || !items?.length) return;
    setSendingAll(true);
    try {
      if (!(await ensureAiDisclosure())) return;
      const results = [];
      for (const entry of items) results.push(await send(entry));
      if (results.some((blocked) => blocked?.code === "ai_disclosure_required") && (await ensureAiDisclosure({ force: true }))) {
        const again = (await uploadQueue.listForAccount(accountId)).filter((row) => row.blocked?.code === "ai_disclosure_required");
        for (const entry of again) await send(entry);
      }
    } finally {
      setSendingAll(false);
      await load();
    }
  };

  /** Ask the family again, from the row; the row's line then says it is asked. */
  const requestConsent = async (entry, student) => {
    if (!student?.student_id || busyId) return;
    setBusyId(entry.id);
    try {
      const res = await api.post(`/students/${student.student_id}/consent/resend`);
      toast.success(`Consent request sent to ${res.data?.sent_to || "the recipient"}.`);
      await fetchStudents();
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't send the consent request."));
    } finally {
      setBusyId(null);
    }
  };

  const discard = async (entry) => {
    if (!window.confirm("Discard this recording? It is deleted from this browser and cannot be recovered.")) return;
    await uploadQueue.remove(entry.id).catch(() => {});
    setItems((rows) => (rows || []).filter((row) => row.id !== entry.id));
  };

  const renderAction = (entry, fix, student) => {
    if (!fix.action) return null;
    const busy = busyId === entry.id;
    switch (fix.action) {
      case "retry":
        return <Button variant="ghost" size="sm" disabled={busy || sendingAll} onPress={() => sendOne(entry)} data-testid={`saved-retry-${entry.id}`}>{busy ? "Sending…" : "Try again"}</Button>;
      case "consent":
        return <Button size="sm" disabled={busy} onPress={() => requestConsent(entry, student)} data-testid={`saved-consent-${entry.id}`}>{busy ? "Sending…" : fix.label}</Button>;
      case "plan":
        return <Button variant="ghost" size="sm" onPress={() => navigate("/settings")} data-testid={`saved-plan-${entry.id}`}>Plan &amp; usage</Button>;
      case "notice":
        return <Button variant="ghost" size="sm" disabled={busy || sendingAll} onPress={() => sendOne(entry, { force: true })} data-testid={`saved-notice-${entry.id}`}>{busy ? "Sending…" : "Review notice"}</Button>;
      default:
        return null;
    }
  };

  return (
    <div data-testid="saved-recordings" className="mx-auto w-full max-w-[760px] px-5 py-8 md:px-8">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <Eyebrow>Waiting to send</Eyebrow>
          <h1 className="mt-1 font-serif text-[34px] font-light leading-tight tracking-tight text-[var(--text-primary)]">
            Saved recordings
          </h1>
          <p className="mt-2 font-sans text-[14px] leading-5 text-[var(--text-secondary)]">
            Kept in this browser until they’re sent. Hans deletes nothing here.
          </p>
        </div>
        {items?.length > 0 && (
          <Button
            className="mb-1 shrink-0"
            disabled={sendingAll || !!busyId}
            onPress={sendAll}
            data-testid="saved-send-all"
          >
            {sendingAll ? "Sending…" : "Send all"}
          </Button>
        )}
      </header>

      {items !== null && items.length === 0 && (
        <Card className="text-center" style={{ padding: 36 }} data-testid="saved-empty">
          <CheckCircle2 className="mx-auto h-9 w-9 text-[var(--text-tertiary)]" aria-hidden="true" />
          <p className="mt-3 font-sans text-[15px] text-[var(--text-secondary)]">Nothing waiting. Every recording has been sent.</p>
        </Card>
      )}

      <ul className="space-y-2.5">
        {(items || []).map((entry) => {
          const student = studentFor(entry);
          const fix = savedFix(entry.blocked, { student });
          const who = entry.kind === "class"
            ? (entry.label || "Class").replace(/ \(class\)$/, "")
            : (studentName(student) || entry.label || "Student");
          const Icon = REASON_ICON[entry.blocked?.code] || CloudUpload;
          const warn = fix.action === "consent" || fix.action === "plan";
          return (
            <li key={entry.id}>
              <Card padding={16} data-testid={`saved-row-${entry.id}`}>
                <p className="truncate font-sans text-[16px] font-semibold text-[var(--text-primary)]">{entry.title}</p>
                <p className="mt-0.5 truncate font-sans text-[13px] text-[var(--text-tertiary)]">
                  {[who, clipLength(entry.duration), savedWhen(entry.createdAt)].filter(Boolean).join(" · ")}
                </p>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--studio-divider)] pt-3">
                  <p className={`flex min-w-0 flex-1 items-center gap-1.5 font-sans text-[13px] ${warn ? "text-[var(--state-warning)]" : "text-[var(--text-tertiary)]"}`}>
                    <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="min-w-0" data-testid={`saved-state-${entry.id}`}>{fix.line}</span>
                  </p>
                  <span className="flex shrink-0 items-center gap-3">
                    {renderAction(entry, fix, student)}
                    <button
                      type="button"
                      onClick={() => discard(entry)}
                      disabled={busyId === entry.id || sendingAll}
                      className="font-sans text-[13px] text-[var(--text-tertiary)] underline hover:text-[var(--text-primary)] disabled:opacity-50"
                      data-testid={`saved-discard-${entry.id}`}
                    >
                      Discard
                    </button>
                  </span>
                </div>
              </Card>
            </li>
          );
        })}
      </ul>

      {aiDisclosureDialog}
    </div>
  );
}
